import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';

interface NumberFieldProps {
  value: number | undefined;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  /**
   * Value applied when the field is cleared and loses focus. When omitted, an
   * empty field reverts to its previous value on blur (non-destructive) — so a
   * stray clear never silently zeroes an unrelated prop (opacity, size, …).
   */
  emptyValue?: number;
}

const toDisplay = (value: number | undefined): string =>
  value === undefined || Number.isNaN(value) ? '' : String(Number(value.toFixed(2)));

const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChange,
  step = 1,
  min,
  max,
  placeholder,
  emptyValue,
}) => {
  const external = toDisplay(value);
  // Local draft so the field can be temporarily empty mid-edit. A fully
  // controlled input rejected the empty string (parseFloat('') is NaN, so no
  // onChange fired and React forced the old value back) — deleting the only
  // digit appeared to do nothing.
  const [draft, setDraft] = useState(external);

  // Adopt the external value when it changes from outside the field, but leave
  // an in-progress draft alone when it already parses to the current value — so
  // a freshly-cleared field stays empty and a partial decimal like "0." survives.
  useEffect(() => {
    setDraft((d) => (parseFloat(d) === value ? d : external));
  }, [external, value]);

  return (
    <Input
      type="number"
      value={draft}
      step={step}
      min={min}
      max={max}
      placeholder={placeholder}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const v = parseFloat(raw);
        if (!Number.isNaN(v)) onChange(v);
      }}
      onBlur={() => {
        if (draft === '') {
          if (emptyValue !== undefined && emptyValue !== value) onChange(emptyValue);
          setDraft(toDisplay(emptyValue ?? value));
        }
      }}
    />
  );
};

export default NumberField;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 4px 6px;
  font-size: 13px;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: #9ca3af;
  }
`;
